import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProgressSteps from '../components/ToolBox/ProgressSteps';
import Step1_UrlInput from '../components/ToolBox/QAKnowledgeBaseGenerator/Step1_UrlInput';
import Step2_Scraping from '../components/ToolBox/QAKnowledgeBaseGenerator/Step2_Scraping';
import Step3_Preview from '../components/ToolBox/QAKnowledgeBaseGenerator/Step3_Preview';
import Step4_Complete from '../components/ToolBox/QAKnowledgeBaseGenerator/Step4_Complete';
import { useToolBox } from '../contexts/ToolBoxContext';

const MODE_TEXT = 'text';
const MODE_PRICING = 'pricing';

const QAKnowledgeBaseGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { qaKnowledgeBaseState, updateQAKnowledgeBaseState, clearQAKnowledgeBaseState } = useToolBox();

  const [mode, setMode] = useState(() => qaKnowledgeBaseState?.mode || MODE_TEXT);
  const [currentStep, setCurrentStep] = useState(() => qaKnowledgeBaseState?.currentStep || 0);
  const [urls, setUrls] = useState(() => qaKnowledgeBaseState?.urls || []);
  const [imageFiles, setImageFiles] = useState(() => qaKnowledgeBaseState?.imageFiles || []);
  const [clid, setClid] = useState(() => qaKnowledgeBaseState?.clid || searchParams.get('clid') || '');
  const [scrapingResults, setScrapingResults] = useState(() => qaKnowledgeBaseState?.scrapingResults || []);
  const [importSummary, setImportSummary] = useState(() => qaKnowledgeBaseState?.importSummary || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (qaKnowledgeBaseState && qaKnowledgeBaseState.initialized) {
      setMode(qaKnowledgeBaseState.mode || MODE_TEXT);
      setCurrentStep(qaKnowledgeBaseState.currentStep || 0);
      setUrls(qaKnowledgeBaseState.urls || []);
      setImageFiles(qaKnowledgeBaseState.imageFiles || []);
      setClid(qaKnowledgeBaseState.clid || searchParams.get('clid') || '');
      setScrapingResults(qaKnowledgeBaseState.scrapingResults || []);
      setImportSummary(qaKnowledgeBaseState.importSummary || null);
    }
  }, [qaKnowledgeBaseState, searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateQAKnowledgeBaseState({
        initialized: true,
        mode,
        currentStep,
        urls,
        imageFiles,
        clid,
        scrapingResults,
        importSummary,
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [mode, currentStep, urls, imageFiles, clid, scrapingResults, updateQAKnowledgeBaseState]);

  const resetSteps = () => {
    setUrls([]);
    setImageFiles([]);
    setScrapingResults([]);
    setImportSummary(null);
    setCurrentStep(0);
    setError('');
  };

  const handleModeChange = (newMode) => {
    if (mode === newMode) return;
    setMode(newMode);
    resetSteps();
  };

  const hasSourceInput = mode === MODE_PRICING ? (urls.length > 0 || imageFiles.length > 0) : urls.length > 0;

  const handleStep1Next = () => {
    if (!hasSourceInput) {
      setError(mode === MODE_PRICING ? 'Add at least one image URL or upload a pricing image' : 'Please add at least one URL');
      return;
    }
    setError('');
    setCurrentStep(1);
  };

  const handleStep2Complete = (results) => {
    setScrapingResults(results);
    setCurrentStep(2);
  };

  const handleStep3Complete = (result) => {
    setLoading(false);
    if (result?.success) {
      setImportSummary(result);
      if (result.clid) {
        setClid(result.clid);
      }
      setCurrentStep(3);
    }
  };

  const handleRestartFlow = () => {
    clearQAKnowledgeBaseState();
    setUrls([]);
    setImageFiles([]);
    setScrapingResults([]);
    setImportSummary(null);
    setCurrentStep(0);
    setError('');
  };

  const handleClearState = () => {
    if (window.confirm('Are you sure you want to clear all progress? This cannot be undone.')) {
      clearQAKnowledgeBaseState();
      setMode(MODE_TEXT);
      setCurrentStep(0);
      setUrls([]);
      setImageFiles([]);
      setClid('');
      setScrapingResults([]);
      setImportSummary(null);
      setError('');
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setError('');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <div className="flex justify-between items-center mb-2">
            <div className="flex-1"></div>
            <h1 className="text-3xl font-bold text-gray-900 flex-1">QA Knowledge Base Generator</h1>
            <div className="flex-1 flex justify-end">
              {(currentStep > 0 || urls.length > 0 || imageFiles.length > 0) && (
                <button
                  onClick={handleClearState}
                  className="text-sm text-red-600 hover:text-red-700 px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                  title="Clear all progress"
                >
                  Clear Progress
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-600">
            {mode === MODE_PRICING
              ? 'Extract pricing lists and generate Q&A pairs from images'
              : 'Extract Q&A pairs from web pages for knowledge base'}
          </p>
          {(currentStep > 0 || urls.length > 0 || imageFiles.length > 0) && (
            <p className="text-sm text-blue-600 mt-2">
              💾 Your progress is saved. You can navigate away and return anytime.
            </p>
          )}
        </div>

        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-l-lg ${
                mode === MODE_TEXT
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleModeChange(MODE_TEXT)}
            >
              Text
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border border-gray-200 rounded-r-lg ${
                mode === MODE_PRICING
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleModeChange(MODE_PRICING)}
            >
              Pricing List
            </button>
          </div>
        </div>

        <ProgressSteps currentStep={currentStep} totalSteps={4} />

        {currentStep === 0 && (
          <Step1_UrlInput
            mode={mode}
            urls={urls}
            setUrls={setUrls}
            imageFiles={imageFiles}
            setImageFiles={setImageFiles}
            onNext={handleStep1Next}
            loading={loading}
            error={error}
          />
        )}

        {currentStep === 1 && (
          <Step2_Scraping
            mode={mode}
            urls={urls}
            imageFiles={imageFiles}
            onComplete={handleStep2Complete}
            onBack={() => goToStep(0)}
            loading={loading}
            error={error}
          />
        )}

        {currentStep === 2 && (
          <Step3_Preview
            mode={mode}
            results={scrapingResults}
            clid={clid}
            onBack={() => goToStep(1)}
            onComplete={handleStep3Complete}
            onRestart={handleRestartFlow}
            loading={loading}
            error={error}
            setLoading={setLoading}
            setError={setError}
          />
        )}

        {currentStep === 3 && (
          <Step4_Complete
            summary={importSummary}
            clid={clid}
            onRestart={handleRestartFlow}
          />
        )}
      </div>
    </div>
  );
};

export default QAKnowledgeBaseGenerator;

